import { useEffect, useMemo } from "react";
import { Link } from "wouter";
import { ArrowRight, Award, BookOpen, CheckCircle2, ClipboardList, Clock, FileText, GraduationCap, PlayCircle, Sparkles, Target } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useCandidate,
  useCandidatesList,
  useEffectiveUser,
  useEnrollmentsForCandidate,
  useExamAttemptsForCandidate,
  useExpedientes,
  useLmsAvancePct,
  useLmsProgress,
  useLmsRoute,
  useStandards,
} from "@/store/hooks";

export default function CandidateDashboard() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("alumno"), [setRole]);

  // Identify the candidate: prefer the "viewed as" user if it's an alumno; otherwise first seeded candidate.
  const effective = useEffectiveUser();
  const candList = useCandidatesList();
  const fallbackId = candList[0]?.id;
  const candidateId =
    effective && effective.roles.includes("alumno") ? effective.id : fallbackId;
  const me = useCandidate(candidateId);

  const enrollments = useEnrollmentsForCandidate(candidateId);
  const standards = useStandards();
  const expedientes = useExpedientes();
  const attempts = useExamAttemptsForCandidate(candidateId);

  const inscritos = useMemo(() => {
    const rows = [] as Array<{ codigo: string; standard: typeof standards[string]; expediente: typeof expedientes[string] | undefined }>;
    for (const code of enrollments) {
      const s = standards[code];
      if (!s) continue;
      const exp = Object.values(expedientes).find((e) => e.candidatoId === candidateId && e.estandar === code);
      rows.push({ codigo: code, standard: s, expediente: exp });
    }
    return rows;
  }, [enrollments, standards, expedientes, candidateId]);

  if (!me) {
    return (
      <AppLayout pageTitle="Mi ruta formativa">
        <Card>
          <div style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-600)" }}>
            No hay candidato activo. Pida a un administrador inscribirle a un estándar.
          </div>
        </Card>
      </AppLayout>
    );
  }

  const primary = inscritos[0];
  const lmsExpId = primary?.expediente?.id;
  const lmsCodigo = primary?.codigo;
  const lmsRoute = useLmsRoute(lmsCodigo);
  const lmsProgress = useLmsProgress(lmsExpId);
  const lmsPct = useLmsAvancePct(lmsExpId, lmsCodigo);
  const nextLmsModule = useMemo(() => {
    if (!lmsRoute) return undefined;
    return lmsRoute.modules.find((m) => !lmsProgress.completedModuleIds.includes(m.id));
  }, [lmsRoute, lmsProgress.completedModuleIds]);
  const lmsReady = lmsPct >= 80;

  return (
    <AppLayout pageTitle="Mi ruta formativa">
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card style={{ background: "linear-gradient(135deg, var(--cc-navy) 0%, #243259 100%)", color: "white", border: "none" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.6)", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>BIENVENIDO</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: "white", marginBottom: 6 }} data-testid="text-candidate-name">{me.nombre}</h1>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{me.unidad} · {me.rango} · Matrícula {me.matricula}</div>
          {primary && (
            <div style={{ marginTop: 16, padding: 12, background: "rgba(255,255,255,0.08)", borderRadius: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontSize: 12 }}>Avance · {primary.codigo}</span>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{me.progreso}%</span>
              </div>
              <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 999, height: 8, overflow: "hidden" }}>
                <div style={{ background: "var(--cc-teal-light)", width: `${me.progreso}%`, height: "100%" }} />
              </div>
            </div>
          )}
          <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
            {primary && (
              <>
                <Link href={`/alumno/diagnostico?codigo=${primary.codigo}`} data-testid="link-continue">
                  <PrimaryButton variant="dark">
                    <PlayCircle size={14} /> Continuar diagnóstico
                  </PrimaryButton>
                </Link>
                <Link href={`/alumno/expediente?codigo=${primary.codigo}`} data-testid="link-my-file">
                  <PrimaryButton variant="ghost">Ver mi expediente</PrimaryButton>
                </Link>
              </>
            )}
            <Link href="/alumno/historial" data-testid="link-history">
              <PrimaryButton variant="ghost"><FileText size={14} /> Historial</PrimaryButton>
            </Link>
          </div>
        </Card>

        <Card>
          <SectionTitle>Próximas actividades</SectionTitle>
          <Activity icon={ClipboardList} title="Diagnóstico" subtitle="Hoy · 11:00" tone="var(--cc-teal)" />
          <Activity icon={BookOpen} title="Sesión teórica 2" subtitle="Mañana · 09:00" tone="var(--cc-navy)" />
          <Activity icon={Target} title="Práctica en circuito" subtitle="23 mayo · 14:00" tone="#0891b2" />
          <Activity icon={Award} title="Cierre y dictamen" subtitle="27 mayo · 10:00" tone="#16a34a" />
        </Card>
      </div>

      {lmsRoute && lmsCodigo && (
        <Card testId="alumno-lms-widget">
          <SectionTitle action={
            <Link href="/alumno/ruta" data-testid="link-open-ruta-dashboard">
              <PrimaryButton size="sm" variant="outline"><BookOpen size={12} /> Abrir ruta</PrimaryButton>
            </Link>
          }>Ruta formativa · {lmsCodigo}</SectionTitle>
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: lmsReady ? "var(--cc-green-bg)" : "var(--cc-teal-bg)", color: lmsReady ? "var(--cc-green)" : "var(--cc-teal)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GraduationCap size={20} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }} data-testid="text-lms-pct">
                {lmsProgress.completedModuleIds.length} de {lmsRoute.modules.length} módulos · {lmsPct}%
              </div>
              <div style={{ background: "var(--cc-gray-100)", borderRadius: 999, height: 6, marginTop: 6, overflow: "hidden" }}>
                <div style={{ background: lmsReady ? "var(--cc-green)" : "var(--cc-teal)", width: `${lmsPct}%`, height: "100%" }} />
              </div>
            </div>
            {lmsReady ? <Badge tone="green">✓ Listo para evaluación</Badge> : <Badge tone="amber">Faltan {80 - lmsPct}%</Badge>}
          </div>
          {nextLmsModule && (
            <div style={{ marginTop: 10, padding: 10, background: "var(--cc-gray-50, #fafbfc)", border: "1px solid var(--cc-gray-100)", borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 12, color: "var(--cc-gray-700)", flex: 1 }}>
                Siguiente módulo recomendado: <strong data-testid="text-lms-next">{nextLmsModule.titulo}</strong>
                <span style={{ marginLeft: 6, color: "var(--cc-gray-500)" }}>· {nextLmsModule.duracionMin} min</span>
              </div>
              <Link
                href={nextLmsModule.kind === "simulador-preop" ? `/alumno/ruta/simulador/${nextLmsModule.id}` : `/alumno/ruta/modulo/${nextLmsModule.id}`}
                data-testid="link-lms-next"
              >
                <PrimaryButton size="sm">Continuar <ArrowRight size={11} /></PrimaryButton>
              </Link>
            </div>
          )}
        </Card>
      )}

      <Card style={{ marginTop: 16 }}>
        <SectionTitle>Estándares en los que estoy inscrito</SectionTitle>
        {inscritos.length === 0 ? (
          <div style={{ padding: 18, color: "var(--cc-gray-600)", fontSize: 13 }}>
            Aún no está inscrito en ningún estándar. El administrador podrá inscribirle desde un grupo de formación.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            {inscritos.map((row) => {
              const exp = row.expediente;
              const diagDone = !!(exp && exp.diagnostico.aplicado);
              const teoDone = !!(exp && exp.examenTeorico.aplicado);
              const certDone = !!(exp && exp.certificado.emitido);
              return (
                <div
                  key={row.codigo}
                  data-testid={`card-enroll-${row.codigo}`}
                  style={{ padding: 14, border: "1px solid var(--cc-gray-100)", borderRadius: 10, background: "white" }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontWeight: 700, color: "var(--cc-navy)" }}>{row.codigo}</div>
                    <Badge tone={certDone ? "green" : "teal"}>{certDone ? "Certificado" : "En curso"}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginBottom: 10, lineHeight: 1.4 }}>
                    {row.standard.titulo}
                  </div>
                  <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap" }}>
                    <Badge tone={diagDone ? "green" : "amber"}>Diag {diagDone ? "✓" : "·"}</Badge>
                    <Badge tone={teoDone ? "green" : "amber"}>Teórico {teoDone ? "✓" : "·"}</Badge>
                    <Badge tone={certDone ? "green" : "amber"}>Cert {certDone ? "✓" : "·"}</Badge>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <Link href={`/alumno/expediente?codigo=${row.codigo}`} data-testid={`link-exp-${row.codigo}`}>
                      <PrimaryButton variant="ghost">Expediente</PrimaryButton>
                    </Link>
                    <Link href={`/alumno/diagnostico?codigo=${row.codigo}`} data-testid={`link-diag-${row.codigo}`}>
                      <PrimaryButton variant="ghost">Diagnóstico</PrimaryButton>
                    </Link>
                    <Link href={`/alumno/examen-teorico?codigo=${row.codigo}`} data-testid={`link-teo-${row.codigo}`}>
                      <PrimaryButton variant="ghost">Examen</PrimaryButton>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Stat icon={ClipboardList} label="Intentos diagnósticos" value={String(attempts.filter((a) => a.kind === "diagnostico").length)} tone="gray" />
        <Stat icon={BookOpen} label="Intentos teóricos" value={String(attempts.filter((a) => a.kind === "teorico").length)} tone="gray" />
        <Stat icon={Award} label="Estándares inscritos" value={String(inscritos.length)} tone="green" />
      </div>

      <Link href="/alumno/ia" data-testid="link-quick-ia" style={{ textDecoration: "none" }}>
        <Card style={{ marginTop: 16, cursor: "pointer", background: "var(--cc-teal-bg)", border: "1px solid #a5f3fc", display: "flex", gap: 14, alignItems: "center" }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--cc-teal)", color: "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Sparkles size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Pregúntale a CertiAI</div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginTop: 2 }}>Tu coach personal: avance, evidencias, práctica y dictamen.</div>
          </div>
          <ArrowRight size={16} color="var(--cc-teal)" />
        </Card>
      </Link>

      {primary && (
        <div style={{ marginTop: 20, textAlign: "right" }}>
          <Link href={`/alumno/diagnostico?codigo=${primary.codigo}`} data-testid="link-start-diag">
            <PrimaryButton>Iniciar / repetir diagnóstico <ArrowRight size={13} /></PrimaryButton>
          </Link>
        </div>
      )}
    </AppLayout>
  );
}

function Activity({ icon: Icon, title, subtitle, tone }: { icon: React.ComponentType<{ size?: number; color?: string }>; title: string; subtitle: string; tone: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--cc-gray-100)" }}>
      <div style={{ width: 30, height: 30, borderRadius: 8, background: tone, color: "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Icon size={15} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{subtitle}</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone }: { icon: React.ComponentType<{ size?: number; color?: string }>; label: string; value: string; tone: "green" | "amber" | "gray" }) {
  const c = { green: "var(--cc-green)", amber: "var(--cc-amber)", gray: "var(--cc-gray-400)" }[tone];
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Icon size={16} color={c} />
        <span style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600 }}>{label.toUpperCase()}</span>
      </div>
      <div style={{ marginTop: 6, fontSize: 20, fontWeight: 700, color: "var(--cc-navy)" }}>{value}</div>
    </Card>
  );
}
